import React from 'react';
import { MdEditNote, MdIosShare, MdPeople } from 'react-icons/md';
import { IoMdTrash } from 'react-icons/io';
import { Modal as ModalAnt } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Divider from 'legacy/common/components/wysiwyg-form/Divider';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import { useDeleteEvent } from '../../hooks';
import { EventForUser } from '../../types';

type ManageEventProps = {
  event: EventForUser;
  onClickGuestPreview: () => void;
  onClickViewShareOptions: () => void;
  toggleManageEvent: () => void;
  visible: boolean;
};

const MANAGE_EVENT_OPTIONS = [
  {
    condition: (event) => event.isOrganizer,
    id: 'button-edit-event',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=edit`);
      },
    label: 'Event details',
    icon: <MdEditNote size={24} />
  },
  {
    condition: (event) => event.isOrganizer || event.isScanner,
    id: 'button-manage-guests',
    onClick:
      ({ onClickGuestPreview }) =>
      () => {
        onClickGuestPreview();
      },
    label: 'Manage guests',
    icon: <MdPeople size={24} />
  },
  {
    condition: (event) => event.isOrganizer || event.isPhotographer,
    featureFlag: FLAGS.ENABLE_EVENT_BULK_PHOTO_UPLOAD,
    id: 'button-upload-moments',
    onClick:
      ({ eventId, router }) =>
      () => {
        router.push(`/events/${eventId}?view=upload-moments`);
      },
    label: 'Upload Moments',
    icon: (
      <img src="/img/events/create/moments.svg" className="w-[24px] h-[24px]" />
    )
  }
];

const ManageEvent: React.FC<ManageEventProps> = ({
  event,
  onClickGuestPreview,
  onClickViewShareOptions,
  toggleManageEvent,
  visible
}) => {
  const router = useRouter();
  const deleteEventCommand = useDeleteEvent();
  const flags = useFlags();

  const onOptionClick = (action) => () => {
    toggleManageEvent();
    action();
  };

  const onDeleteDraft = () => {
    toggleManageEvent();

    ModalAnt.confirm({
      title: 'Delete event',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete this event${
        event.title ? `: "${event.title}"` : ''
      }?`,
      centered: true,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteEventCommand.mutate(event._id, {
          onSuccess: () => {
            router.push('/host');
          }
        });
      }
    });
  };

  return (
    <Modal
      type={ModalType.SMALL}
      onClose={toggleManageEvent}
      autoHeight
      hideCloseButton
      visible={visible}
    >
      <div className="p-4 flex flex-col">
        <ul>
          {MANAGE_EVENT_OPTIONS.map((option) =>
            option.condition(event) &&
            (option.featureFlag ? flags[option.featureFlag] : true) ? (
              <li
                key={option.id}
                data-testid={option.id}
                className="font-normal text-base cursor-pointer py-4 flex align-center gap-2 text-neutral-600"
                onClick={onOptionClick(
                  option.onClick({
                    eventId: event._id,
                    router,
                    onClickGuestPreview
                  })
                )}
              >
                {option.icon}
                {option.label}
              </li>
            ) : null
          )}
        </ul>
        <Divider size={4} />
        {!(event.private && !event.isOrganizer) && (
          <div className="flex flex-col gap-2 mt-6">
            <Button
              type={ButtonType.Inverted}
              onClick={onOptionClick(onClickViewShareOptions)}
              icon={<MdIosShare size={20} />}
            >
              Share event
            </Button>
          </div>
        )}
        {event.isOrganizer && (
          <>
            <Divider size={6} />
            <div className="mt-6">
              <Button
                type={ButtonType.Danger}
                icon={<IoMdTrash size={20} />}
                onClick={onDeleteDraft}
              >
                Delete event
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ManageEvent;
